import type { ICellRendererParams } from 'ag-grid-community';
import type { PromptSourceItem } from '../types';
import { BrandFavicon } from '../components/BrandFavicon';
import { SourceDisplayTypeBadge } from '../components/sources/SourceDisplayTypeBadge';
import { getHelpHtml } from '../utils/help/help-content';
import { formatSourceCount, formatSourceDate, sourceDisplayLabel } from '../utils/source-insights';

function columnHelp(key: string): string | undefined {
  return getHelpHtml(key) || undefined;
}

export function promptSourcesColumnConfig() {
  return [
    {
      field: 'host',
      title: 'منبع',
      tooltip: columnHelp('sections.identified_sources_table'),
      type: 'string',
      flex: 1,
      minWidth: 220,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptSourceItem>) => {
        const row = params.data;
        if (!row) return null;
        const label = sourceDisplayLabel(row.host, row.displayName);
        const showHostSubtitle =
          Boolean(row.displayName?.trim()) &&
          row.displayName.trim().toLowerCase() !== row.host.toLowerCase();
        return (
          <div className="d-flex align-items-center gap-2 h-100">
            <BrandFavicon host={row.host} name={label} size="sm" />
            <div className="min-w-0 lh-sm">
              <div className="fw-medium text-truncate">{label}</div>
              {showHostSubtitle ? (
                <div className="fs--2 text-body-tertiary text-truncate">{row.host}</div>
              ) : null}
            </div>
          </div>
        );
      },
    },
    {
      field: 'displayType',
      title: 'نوع',
      tooltip: columnHelp('sections.source_types'),
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptSourceItem>) =>
        params.data ? <SourceDisplayTypeBadge type={params.data.displayType} /> : null,
    },
    {
      field: 'citations',
      title: 'Citationها',
      tooltip: columnHelp('metrics.total_citations'),
      type: 'string',
      width: 120,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptSourceItem>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourceCount(params.data.citations)}
          </span>
        ) : null,
    },
    {
      field: 'aiResponses',
      title: 'پاسخ‌های AI',
      tooltip: columnHelp('tables.ai_responses'),
      type: 'string',
      width: 130,
      searchable: true,
      cellRenderer: (params: ICellRendererParams<PromptSourceItem>) =>
        params.data ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatSourceCount(params.data.aiResponses)}
          </span>
        ) : null,
    },
    {
      field: 'urls',
      title: 'آدرس‌ها',
      tooltip: columnHelp('tables.source_urls'),
      type: 'string',
      flex: 1,
      minWidth: 220,
      sortable: false,
      searchable: false,
      cellStyle: { direction: 'ltr', textAlign: 'left' },
      cellRenderer: (params: ICellRendererParams<PromptSourceItem>) => {
        const row = params.data;
        if (!row?.urls[0]) return <span>—</span>;
        return (
          <div dir="ltr" className="w-100" style={{ textAlign: 'left' }}>
            <a
              href={row.urls[0].url}
              target="_blank"
              rel="noreferrer"
              className="d-block text-truncate"
            >
              {row.urls[0].url}
            </a>
            {row.urls.length > 1 && (
              <span className="fs--2 text-500">+{row.urls.length - 1} مورد دیگر</span>
            )}
          </div>
        );
      },
    },
    {
      field: 'lastSeenAt',
      title: 'آخرین مشاهده',
      tooltip: columnHelp('tables.last_seen'),
      type: 'string',
      width: 160,
      searchable: true,
      valueFormatter: (p: { value: string | null }) => formatSourceDate(p.value),
    },
  ];
}
